import { useEffect, useMemo, useState } from 'preact/hooks';
import { DashboardApi, type DiffDelta, HttpError } from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

interface ScanListItem {
  id: string;
  productName: string;
  productVersion: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | null;
  startedAt: string;
  buildRef: string | null;
}

interface DiffResult {
  prev: { id: string; productName: string; productVersion: string };
  curr: { id: string; productName: string; productVersion: string };
  counts: {
    regressed: number;
    improved: number;
    unchanged: number;
    introduced: number;
    removed: number;
    total: number;
  };
  hasRegressions: boolean;
  regressed: DiffDelta[];
  improved: DiffDelta[];
  introduced: DiffDelta[];
  removed: DiffDelta[];
}

export function Diff({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const [scans, setScans] = useState<ScanListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [productFilter, setProductFilter] = useState<string>('');
  const [prevId, setPrevId] = useState<string>('');
  const [currId, setCurrId] = useState<string>('');
  const [diff, setDiff] = useState<DiffResult | null>(null);
  const [running, setRunning] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: stable refresh
  useEffect(() => {
    let cancelled = false;
    api
      .listScans(org.id)
      .then((r) => {
        if (cancelled) return;
        setScans(r.scans);
        // Auto-pick the most recent product so the user sees a useful default.
        const products = [...new Set(r.scans.map((s) => s.productName))];
        if (products.length > 0 && !productFilter) {
          const first = products[0];
          if (first) setProductFilter(first);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof HttpError ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [org.id]);

  const products = useMemo(() => {
    if (!scans) return [] as string[];
    return [...new Set(scans.map((s) => s.productName))].sort();
  }, [scans]);

  const productScans = useMemo(() => {
    if (!scans) return [] as ScanListItem[];
    return scans
      .filter((s) => (productFilter ? s.productName === productFilter : true))
      .filter((s) => s.status === 'completed')
      .sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1));
  }, [scans, productFilter]);

  // Auto-pick: curr = most recent completed; prev = second most recent.
  useEffect(() => {
    if (productScans.length >= 2) {
      const first = productScans[0];
      const second = productScans[1];
      if (first && second) {
        setCurrId(first.id);
        setPrevId(second.id);
      }
    } else {
      setCurrId('');
      setPrevId('');
    }
    setDiff(null);
  }, [productScans]);

  const runDiff = async (): Promise<void> => {
    if (!prevId || !currId) return;
    if (prevId === currId) {
      setError('Pick two different scans.');
      return;
    }
    setRunning(true);
    setError(null);
    try {
      const r = await api.scanDiff(org.id, currId, prevId);
      setDiff(r as DiffResult);
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <div class="page-header">
        <h1>Scan diff</h1>
        <p>
          Compare two scans of the same product. CI uses the same endpoint to fail builds on
          regression — this page surfaces the verdict for review.
        </p>
      </div>

      {error && <Banner variant="warn">{error}</Banner>}

      <div class="card">
        <h2>Pick two scans</h2>
        {scans === null && (
          <p class="small muted">
            <output>Loading…</output>
          </p>
        )}
        {scans !== null && products.length === 0 && (
          <p class="small muted">
            No scans yet. Run one via the GitHub Action, the scanner-runner CLI, or the API.
          </p>
        )}
        {products.length > 0 && (
          <>
            <div class="field">
              <label htmlFor="diff-product">Product</label>
              <select
                id="diff-product"
                value={productFilter}
                onChange={(e) => {
                  setProductFilter((e.target as HTMLSelectElement).value);
                  setDiff(null);
                }}
              >
                {products.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            {productScans.length < 2 && (
              <p class="small muted">
                Need at least 2 completed scans for this product to compare. You have{' '}
                {productScans.length}.
              </p>
            )}
            {productScans.length >= 2 && (
              <>
                <div class="field" style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label htmlFor="diff-prev">Previous (baseline)</label>
                    <select
                      id="diff-prev"
                      value={prevId}
                      onChange={(e) => {
                        setPrevId((e.target as HTMLSelectElement).value);
                        setDiff(null);
                      }}
                    >
                      {productScans.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.productVersion} · {new Date(s.startedAt).toLocaleString()}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label htmlFor="diff-curr">Current</label>
                    <select
                      id="diff-curr"
                      value={currId}
                      onChange={(e) => {
                        setCurrId((e.target as HTMLSelectElement).value);
                        setDiff(null);
                      }}
                    >
                      {productScans.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.productVersion} · {new Date(s.startedAt).toLocaleString()}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={running || !prevId || !currId}
                  onClick={() => void runDiff()}
                >
                  {running ? 'Diffing…' : 'Run diff'}
                </button>
              </>
            )}
          </>
        )}
      </div>

      {diff && (
        <div class="card">
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <div>
              <h2 style={{ margin: 0 }}>
                {diff.curr.productName} {diff.curr.productVersion} vs {diff.prev.productVersion}
              </h2>
              <p class="small muted">
                Conformance ordering: Supports &gt; Partially Supports &gt; Not Applicable &gt; Not
                Evaluated &gt; Does Not Support. Anything moving down is a regression.
              </p>
            </div>
            {diff.hasRegressions ? (
              <span class="pill pill-fails">{diff.counts.regressed} regressions</span>
            ) : (
              <span class="pill pill-supports">No regressions</span>
            )}
          </div>

          <p class="small muted" style={{ marginTop: 12 }}>
            <strong>{diff.counts.regressed}</strong> regressed ·{' '}
            <strong>{diff.counts.improved}</strong> improved ·{' '}
            <strong>{diff.counts.introduced}</strong> newly evaluated ·{' '}
            <strong>{diff.counts.removed}</strong> no longer evaluated ·{' '}
            <strong>{diff.counts.unchanged}</strong> unchanged · {diff.counts.total} total
          </p>

          {diff.regressed.length > 0 && (
            <DeltaSection title="Regressed" deltas={diff.regressed} variant="fails" />
          )}
          {diff.improved.length > 0 && (
            <DeltaSection title="Improved" deltas={diff.improved} variant="supports" />
          )}
          {diff.introduced.length > 0 && (
            <DeltaSection title="Newly evaluated" deltas={diff.introduced} variant="muted" />
          )}
          {diff.removed.length > 0 && (
            <DeltaSection title="No longer evaluated" deltas={diff.removed} variant="muted" />
          )}
        </div>
      )}
    </>
  );
}

function DeltaSection({
  title,
  deltas,
  variant,
}: {
  title: string;
  deltas: DiffDelta[];
  variant: 'fails' | 'supports' | 'muted';
}): preact.ComponentChildren {
  return (
    <div style={{ marginTop: 12 }}>
      <h3 style={{ margin: '8px 0' }}>
        <span class={`pill pill-${variant}`}>{title}</span> {deltas.length}
      </h3>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {deltas.map((d) => (
          <li key={d.criterion} class="small">
            <strong>{d.criterion}</strong> — {d.summary}
          </li>
        ))}
      </ul>
    </div>
  );
}
